/**
 * Where the concierge panel sits, and how big it is.
 *
 * WHY THIS IS A SHARED MODULE
 * The drag helpers were written for the scripted concierge and lived inside
 * `Concierge.tsx`. The agent panel then shipped without them — so dragging, a
 * capability that already existed and was already tested, silently regressed
 * for anyone on the newer widget. Extracting them is the fix that stops that
 * happening again the next time a third surface appears.
 *
 * EVERYTHING HERE IS DEFENSIVE ABOUT STORED VALUES on purpose. These come from
 * localStorage, which means another tab, an older build, or a person with
 * devtools open. A NaN width or an Infinity coordinate puts the panel somewhere
 * unreachable with no way to get it back — and the panel is the support
 * surface, so "unreachable" means a customer who cannot ask for help.
 */

export interface Point {
  x: number;
  y: number;
}

export interface Size {
  width: number;
  height: number;
}

export const DRAG_KEY = 'iv_concierge_pos';
export const SIZE_KEY = 'iv_concierge_size';

/**
 * How far the pointer must travel before a press counts as a drag.
 *
 * Below this it is a tap, and a tap still toggles the corner — so the control
 * keeps working for keyboard and for anyone who does not think to drag.
 */
export const DRAG_THRESHOLD_PX = 4;

/** Keeps the panel off the very edge when clamped. */
export const VIEWPORT_MARGIN_PX = 8;

/**
 * Size bounds.
 *
 * The minimum is not arbitrary: below roughly this the composer and a single
 * exchange stop fitting together, and a chat you cannot read is worse than one
 * you cannot resize. The maximum keeps it a panel rather than a takeover.
 */
export const MIN_PANEL = { width: 300, height: 260 } as const;
export const MAX_PANEL = { width: 720, height: 900 } as const;

/** The size the panel opens at when nobody has chosen one. */
export const DEFAULT_PANEL: Size = { width: 380, height: 520 };

export function readDragPos(): Point | null {
  try {
    const raw = window.localStorage.getItem(DRAG_KEY);
    if (!raw) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) {
      return null;
    }
    const { x, y } = parsed as Partial<Point>;
    if (typeof x !== 'number' || typeof y !== 'number') {
      return null;
    }
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      return null;
    }
    return { x, y };
  } catch {
    return null;
  }
}

/**
 * Hold a dragged position inside the window.
 *
 * Called on every move AND on resize/rotate: a position that was fine on a
 * desktop window is off-screen on a phone, and a panel you cannot reach is
 * worse than one that never moved.
 */
export function clampToViewport(point: Point, width: number, height: number): Point {
  const maxX = Math.max(VIEWPORT_MARGIN_PX, window.innerWidth - width - VIEWPORT_MARGIN_PX);
  const maxY = Math.max(VIEWPORT_MARGIN_PX, window.innerHeight - height - VIEWPORT_MARGIN_PX);
  return {
    x: Math.min(Math.max(point.x, VIEWPORT_MARGIN_PX), maxX),
    y: Math.min(Math.max(point.y, VIEWPORT_MARGIN_PX), maxY),
  };
}

/**
 * Hold a size within both the configured bounds AND the current window.
 *
 * The viewport term matters more than the constant one: a 720px panel saved on
 * a desktop is wider than a phone, and restoring it verbatim would push the
 * close button off-screen.
 */
export function clampSize(size: Size): Size {
  const roomW = Math.max(MIN_PANEL.width, window.innerWidth - VIEWPORT_MARGIN_PX * 2);
  const roomH = Math.max(MIN_PANEL.height, window.innerHeight - VIEWPORT_MARGIN_PX * 2);
  return {
    width: Math.min(Math.max(size.width, MIN_PANEL.width), Math.min(MAX_PANEL.width, roomW)),
    height: Math.min(Math.max(size.height, MIN_PANEL.height), Math.min(MAX_PANEL.height, roomH)),
  };
}

export function readPanelSize(): Size | null {
  try {
    const raw = window.localStorage.getItem(SIZE_KEY);
    if (!raw) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) {
      return null;
    }
    const { width, height } = parsed as Partial<Size>;
    if (typeof width !== 'number' || typeof height !== 'number') {
      return null;
    }
    if (!Number.isFinite(width) || !Number.isFinite(height)) {
      return null;
    }
    // Reject non-positive outright rather than clamping — a stored 0 or -50 is
    // corruption, not a preference, and silently "fixing" it hides the fact.
    if (width <= 0 || height <= 0) {
      return null;
    }
    return { width, height };
  } catch {
    return null;
  }
}

export function writePanelSize(size: Size): void {
  try {
    window.localStorage.setItem(SIZE_KEY, JSON.stringify(size));
  } catch {
    // Size is a convenience; losing it is not worth surfacing to a customer.
  }
}

export function writeDragPos(point: Point): void {
  try {
    window.localStorage.setItem(DRAG_KEY, JSON.stringify(point));
  } catch {
    // As above.
  }
}

export function clearDragPos(): void {
  try {
    window.localStorage.removeItem(DRAG_KEY);
  } catch {
    // As above.
  }
}

/** The eight grab zones around the panel border. */
export type ResizeEdge = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

/** Which cursor each zone shows. Exported so the component cannot drift from it. */
export const EDGE_CURSOR: Record<ResizeEdge, string> = {
  n: 'ns-resize',
  s: 'ns-resize',
  e: 'ew-resize',
  w: 'ew-resize',
  ne: 'nesw-resize',
  sw: 'nesw-resize',
  nw: 'nwse-resize',
  se: 'nwse-resize',
};

/**
 * Apply a resize drag from any edge or corner.
 *
 * ORIGIN IS ALWAYS REQUIRED, and that is a deliberate simplification. The panel
 * defaults to being anchored bottom-right by CSS, where the right and bottom
 * edges cannot move — so dragging the east edge did nothing and only the
 * top-left corner behaved sensibly. The caller now converts an anchored panel
 * to explicit left/top on the first resize, which makes all eight zones behave
 * the same conventional way and removes a whole class of "that edge does
 * nothing" confusion.
 *
 * The rule per axis: dragging a NORTH or WEST zone moves that edge, so the size
 * changes by the negated delta AND the origin follows; dragging SOUTH or EAST
 * leaves the origin alone and grows towards the pointer.
 *
 * The origin moves by how much the size ACTUALLY changed after clamping, never
 * the raw pointer delta — otherwise the panel keeps sliding once it has hit a
 * limit and appears to run away from the cursor.
 */
export function resizeFrom(input: {
  edge: ResizeEdge;
  startSize: Size;
  startOrigin: Point;
  dx: number;
  dy: number;
}): { size: Size; origin: Point } {
  const { edge, startSize, startOrigin, dx, dy } = input;

  const pullsWest = edge === 'w' || edge === 'nw' || edge === 'sw';
  const pullsEast = edge === 'e' || edge === 'ne' || edge === 'se';
  const pullsNorth = edge === 'n' || edge === 'nw' || edge === 'ne';
  const pullsSouth = edge === 's' || edge === 'sw' || edge === 'se';

  const wantedWidth = pullsWest
    ? startSize.width - dx
    : pullsEast
      ? startSize.width + dx
      : startSize.width;
  const wantedHeight = pullsNorth
    ? startSize.height - dy
    : pullsSouth
      ? startSize.height + dy
      : startSize.height;

  const size = clampSize({ width: wantedWidth, height: wantedHeight });

  // Only the edges that actually move the origin shift it, and only by the
  // amount the clamp allowed.
  const takenX = pullsWest ? startSize.width - size.width : 0;
  const takenY = pullsNorth ? startSize.height - size.height : 0;

  return {
    size,
    origin: { x: startOrigin.x + takenX, y: startOrigin.y + takenY },
  };
}
